import type { UserFileRead } from '@/uppy-file-upload/schemas'
import { countExtractedDescendants, groupExtractedFiles } from './group-extracted-files'

/** Sum of extracted descendants for the given file ids within `files` (same-folder grouping). */
export function countExtractedAttachmentsForSelection(files: UserFileRead[], selectedFileIds: readonly string[]): number {
  if (selectedFileIds.length === 0 || files.length === 0) return 0
  const grouped = groupExtractedFiles(files)
  const selected = new Set(selectedFileIds)
  let total = 0
  for (const id of selected) {
    total += countExtractedDescendants(grouped, id)
  }
  return total
}

export function formatAttachmentNoun(count: number): string {
  return count === 1 ? '1 attachment' : `${count} attachments`
}

/**
 * Delete confirmation copy when removing emails also removes their extracted attachments.
 * Returns null when the selection has no grouped attachments to call out.
 */
export function getEmailDeleteScopeCopy(params: {
  fileCount: number
  folderCount: number
  extractedAttachmentCount: number
}): { title: string; description: string } | null {
  const { fileCount, folderCount, extractedAttachmentCount } = params
  if (extractedAttachmentCount <= 0) return null

  const attachments = formatAttachmentNoun(extractedAttachmentCount)

  if (fileCount === 1 && folderCount === 0) {
    return {
      title: `Delete email and ${attachments}?`,
      description: `This email and its ${attachments} will be deleted permanently.`,
    }
  }

  const selectedCount = fileCount + folderCount
  const noun = folderCount > 0 && fileCount > 0 ? 'item' : folderCount > 0 ? 'folder' : 'file'
  const title = `Delete ${selectedCount} ${selectedCount === 1 ? noun : `${noun}s`}?`
  const base =
    folderCount > 0 && fileCount > 0
      ? `${folderCount === 1 ? '1 folder' : `${folderCount} folders`} and ${fileCount === 1 ? '1 file' : `${fileCount} files`} will be deleted permanently.`
      : folderCount > 0
      ? folderCount === 1
        ? '1 folder will be deleted permanently.'
        : `${folderCount} folders will be deleted permanently.`
      : fileCount === 1
      ? '1 file will be deleted permanently.'
      : `${fileCount} files will be deleted permanently.`

  return {
    title,
    description: `${base} Includes ${attachments} from selected emails.`,
  }
}

/**
 * Move dialog title when moving emails also moves their extracted attachments.
 * Returns null when there is nothing extra to call out.
 */
export function getEmailMoveScopeTitle(params: { fileCount: number; folderCount: number; extractedAttachmentCount: number }): string | null {
  const { fileCount, folderCount, extractedAttachmentCount } = params
  if (extractedAttachmentCount <= 0 || folderCount > 0) return null

  const attachments = formatAttachmentNoun(extractedAttachmentCount)
  if (fileCount === 1) return `Move email and ${attachments} to…`
  return `Move ${fileCount} files and ${attachments} to…`
}
